import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { ActionDialog } from '@/components/hr/ActionDialog';
import { printTable } from '@/components/hrPrint';
import { DialogSubject, ErrorNote, Modal, OkNote, Select, Table, errorMessage, todayIso } from '@/components/ui';
import { WORKER_TYPE_TEXT, hrGet, useHrInvalidation, type WorkerType } from '@/hooks/hr';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import { daysText, yearOptions } from './hrScreenHelpers';

export interface Balance {
  leaveTypeId: string;
  code: string;
  nameEn: string;
  isPaid: boolean;
  entitled: string;
  carriedForward: string;
  adjusted: string;
  taken: string;
  pending: string;
  remaining: string;
}

export interface BalanceItem {
  employeeId: string;
  code: string;
  nameEn: string;
  workerType: WorkerType;
  farmCode: string | null;
  balances: Balance[];
}

interface CarryRow {
  employeeId: string;
  code: string;
  nameEn: string;
  leaveType: string;
  leaveTypeId: string;
  remaining: string;
  carried: string;
}

interface CarryResult {
  fromYear: number;
  toYear: number;
  rows: CarryRow[];
  saved: number;
}

function breakdown(b: Balance): string {
  const adjusted = Number(b.adjusted);
  const adj = adjusted === 0 ? '' : ` ${adjusted > 0 ? '+' : '−'} ${daysText(Math.abs(adjusted))} adjusted`;
  return `${daysText(b.entitled)} entitled + ${daysText(b.carriedForward)} brought forward${adj} − ${daysText(b.taken)} taken = ${daysText(b.remaining)} left${
    Number(b.pending) > 0 ? `. ${daysText(b.pending)} waiting for a decision.` : ''
  }`;
}

/** Everyone × every kind of leave for a year: what is left, and how it got there. */
export function LeaveBalances({
  toggle,
  personChip,
  personId,
}: {
  toggle: ReactNode;
  personChip: ReactNode;
  personId: string | null;
}) {
  const { can } = useAuth();
  const invalidate = useHrInvalidation();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const thisYear = Number(todayIso().slice(0, 4));
  const [year, setYear] = useState(String(thisYear));
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('balances');
  const [adjusting, setAdjusting] = useState<BalanceItem | null>(null);
  const [preview, setPreview] = useState<CarryResult | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['hr', 'leave', 'balances', farmIds, year, personId],
    queryFn: () =>
      hrGet<{ year: number; items: BalanceItem[] }>('/api/v1/hr/leave/balances', {
        ...farmScopeQuery(farmIds),
        year,
        employeeId: personId,
      }),
  });
  const rows = list.data?.items ?? [];
  const selected = rows.find((r) => r.employeeId === selectedKey) ?? null;

  // One column per kind of leave anyone here has, in the server's order.
  const types: Array<{ id: string; nameEn: string; isPaid: boolean }> = [];
  for (const r of rows) {
    for (const b of r.balances) {
      if (!types.some((t) => t.id === b.leaveTypeId)) types.push({ id: b.leaveTypeId, nameEn: b.nameEn, isPaid: b.isPaid });
    }
  }

  const carry = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<CarryResult>('/api/v1/hr/leave/carry-forward', { fromYear: Number(year), dryRun }),
    onSuccess: (r, dryRun) => {
      setProblem(null);
      if (dryRun) {
        setPreview(r);
        return;
      }
      setPreview(null);
      invalidate();
      setNote(`Carried forward ${r.saved} ${r.saved === 1 ? 'balance' : 'balances'} from ${r.fromYear} into ${r.toYear}.`);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not carry forward')),
  });

  function print() {
    printTable({
      title: `Leave balances ${year}`,
      subtitle: 'Days left for each kind of leave',
      columns: [
        { key: 'person', header: 'Person' },
        { key: 'farm', header: 'Farm' },
        ...types.map((t) => ({ key: `t${t.id}`, header: t.nameEn })),
      ],
      rows: rows.map((r) => {
        const out: Record<string, unknown> = { person: `${r.code} — ${r.nameEn}`, farm: r.farmCode ?? 'Head office' };
        for (const t of types) {
          const b = r.balances.find((x) => x.leaveTypeId === t.id);
          out[`t${t.id}`] = b ? (b.isPaid ? daysText(b.remaining) : `${daysText(b.taken)} taken`) : '—';
        }
        return out;
      }),
      landscape: types.length > 4,
    });
  }

  const actions: DesktopAction[] = [
    {
      label: 'Adjust balance',
      icon: 'edit',
      onSelect: () => selected && setAdjusting(selected),
      allowed: can('hr.leave.approve'),
      enabled: !!selected && selected.balances.length > 0,
      reason: selected ? 'No leave applies to this person' : 'Choose a person first',
    },
    {
      label: 'Carry forward to next year',
      icon: 'month',
      onSelect: () => carry.mutate(true),
      allowed: can('hr.leave.approve'),
      enabled: !carry.isPending,
      reason: 'Working…',
      separatorBefore: true,
    },
    {
      label: 'Print',
      icon: 'print',
      onSelect: print,
      enabled: rows.length > 0,
      reason: 'Nothing to print',
      separatorBefore: true,
    },
  ];

  const columns: Array<GridColumn<BalanceItem>> = [
    { key: 'person', header: 'Person', width: 210, render: (r) => `${r.code} — ${r.nameEn}` },
    { key: 'farm', header: 'Farm', width: 80, render: (r) => <span className="dim">{r.farmCode ?? 'Head office'}</span> },
    ...types.map(
      (t): GridColumn<BalanceItem> => ({
        key: `t${t.id}`,
        header: t.isPaid ? t.nameEn : `${t.nameEn} (unpaid)`,
        align: 'end',
        width: 130,
        render: (r) => {
          const b = r.balances.find((x) => x.leaveTypeId === t.id);
          if (!b) {
            return (
              <span className="dim" title={`${t.nameEn} does not apply to ${WORKER_TYPE_TEXT[r.workerType].toLowerCase()} staff`}>
                —
              </span>
            );
          }
          if (!b.isPaid) {
            return (
              <span className="dim" title="Unpaid leave has no balance">
                {daysText(b.taken)} taken
              </span>
            );
          }
          return (
            <span className="leavebal" title={breakdown(b)}>
              <strong className={Number(b.remaining) < 0 ? 'leavebal__neg' : undefined}>{daysText(b.remaining)}</strong>
              <span className="dim"> left</span>
              {Number(b.pending) > 0 && <span className="pill pill--warn leavebal__wait">{daysText(b.pending)} waiting</span>}
            </span>
          );
        },
      }),
    ),
  ];

  const tabs: DetailTab[] = selected
    ? [
        {
          id: 'balances',
          label: 'Balances',
          icon: 'leave',
          content: (
            <Table
              count={selected.balances.length}
              empty="No leave applies to this person."
              head={
                <tr>
                  <th>Leave</th>
                  <th className="num">Entitled</th>
                  <th className="num">Brought forward</th>
                  <th className="num">Adjusted</th>
                  <th className="num">Taken</th>
                  <th className="num">Left</th>
                  <th className="num">Waiting</th>
                </tr>
              }
            >
              {selected.balances.map((b) => (
                <tr key={b.leaveTypeId}>
                  <td>
                    {b.nameEn}
                    {!b.isPaid && <span className="dim"> (unpaid)</span>}
                  </td>
                  <td className="num">{daysText(b.entitled)}</td>
                  <td className="num">{daysText(b.carriedForward)}</td>
                  <td className="num">{daysText(b.adjusted)}</td>
                  <td className="num">{daysText(b.taken)}</td>
                  <td className="num">
                    <strong>{b.isPaid ? daysText(b.remaining) : '—'}</strong>
                  </td>
                  <td className="num">{daysText(b.pending)}</td>
                </tr>
              ))}
            </Table>
          ),
        },
      ]
    : [];

  return (
    <div className="workbench">
      <SplitView
        id="hr-leave-balances"
        direction="column"
        initial={420}
        firstLabel="balances list"
        first={
          <div className="workbench__main">
            <div className="filterbar leave__filters">
              <Select
                label="Year"
                value={year}
                onChange={(v) => {
                  setYear(v || String(thisYear));
                  setPreview(null);
                }}
                options={yearOptions(thisYear, 4, 1)}
                placeholder=""
              />
              {personChip}
              <span className="leave__spacer" />
              {toggle}
            </div>
            <ActionBar actions={actions}>
              <span className="muted leave__hint">Hover a number to see how it adds up.</span>
            </ActionBar>
            {note && <OkNote message={note} />}
            {problem && <ErrorNote message={problem} />}
            {list.error && <ErrorNote message={list.error.message} />}
            <DataGrid
              id="hr-leave-balances"
              label="Leave balances"
              columns={columns}
              rows={rows}
              rowKey={(r) => r.employeeId}
              selectedKey={selectedKey}
              onSelect={(k) => {
                setSelectedKey(k);
                setNote(null);
              }}
              actions={(r) => [
                {
                  label: 'Adjust balance',
                  icon: 'edit',
                  onSelect: () => setAdjusting(r),
                  allowed: can('hr.leave.approve'),
                  enabled: r.balances.length > 0,
                  reason: 'No leave applies to this person',
                },
              ]}
              loading={list.isPending}
              empty="Nobody to show."
            />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a person to see how each balance adds up."
            title={
              selected && (
                <span className="withicon">
                  <AppIcon kind="employee" size={14} />
                  <span className="mono">{selected.code}</span> {selected.nameEn} · {year}
                </span>
              )
            }
          />
        }
      />
      <StatusBar>
        <span>Year {year}</span>
        <span>{rows.length} people</span>
        <span>{types.length} kinds of leave</span>
        <span>{selected ? `Chosen: ${selected.code}` : 'Nobody chosen'}</span>
      </StatusBar>

      {adjusting && (
        <ActionDialog
          title="Adjust leave"
          icon="edit"
          subject={
            <DialogSubject
              icon="employee"
              code={adjusting.code}
              name={adjusting.nameEn}
              tags={[
                { icon: 'people', label: WORKER_TYPE_TEXT[adjusting.workerType] },
                { icon: 'farm', label: adjusting.farmCode ?? 'Head office' },
                { icon: 'month', label: String(year) },
              ]}
            />
          }
          intro={`Adds days to ${year}'s balance, or takes them away. Saved with your reason in the audit history.`}
          fields={[
            {
              key: 'leaveTypeId',
              label: 'Leave',
              type: 'select',
              required: true,
              options: adjusting.balances.map((b) => ({ value: b.leaveTypeId, label: b.nameEn })),
            },
            {
              key: 'days',
              label: 'Days',
              type: 'number',
              required: true,
              hint: 'Use a minus sign to take days away, e.g. -1.5. Whole or half days.',
            },
            { key: 'reason', label: 'Why', type: 'textarea', required: true },
          ]}
          initial={{ leaveTypeId: adjusting.balances[0]?.leaveTypeId ?? '' }}
          submitLabel="Adjust"
          onClose={() => setAdjusting(null)}
          onSubmit={async (v) => {
            const days = Number(v.days);
            if (!Number.isFinite(days) || days === 0) throw new Error('Enter the days to add or take away.');
            if (!Number.isInteger(days * 2)) throw new Error('Days must be whole or half days.');
            await api.post('/api/v1/hr/leave/balances/adjust', {
              employeeId: adjusting.employeeId,
              leaveTypeId: v.leaveTypeId,
              year: Number(year),
              days: String(days),
              reason: v.reason?.trim(),
            });
            const type = adjusting.balances.find((b) => b.leaveTypeId === v.leaveTypeId)?.nameEn ?? 'leave';
            invalidate();
            setProblem(null);
            setNote(
              `${adjusting.nameEn}: ${days > 0 ? 'added' : 'took away'} ${daysText(Math.abs(days))} ${Math.abs(days) === 1 ? 'day' : 'days'} of ${type} for ${year}.`,
            );
          }}
        />
      )}

      {preview && (
        <Modal title={`Carry forward ${preview.fromYear} to ${preview.toYear}`} onClose={() => setPreview(null)} wide>
          <h2 className="withicon">
            <AppIcon kind="month" />
            Carry forward {preview.fromYear} to {preview.toYear}
          </h2>
          <p className="muted">
            Unused leave moves into {preview.toYear}, up to each kind of leave's limit. Nothing changes until you confirm. Running it
            again later replaces what was carried before.
          </p>
          <Table
            count={preview.rows.length}
            empty="Nothing to carry forward: no kind of leave carries over, or nobody has any left."
            head={
              <tr>
                <th>Person</th>
                <th>Leave</th>
                <th className="num">Left at the end of {preview.fromYear}</th>
                <th className="num">Carried into {preview.toYear}</th>
              </tr>
            }
          >
            {preview.rows.map((r) => (
              <tr key={`${r.employeeId}:${r.leaveTypeId}`}>
                <td>
                  <span className="mono">{r.code}</span> {r.nameEn}
                </td>
                <td>{r.leaveType}</td>
                <td className="num">{daysText(r.remaining)}</td>
                <td className="num">
                  <strong>{daysText(r.carried)}</strong>
                </td>
              </tr>
            ))}
          </Table>
          {problem && <ErrorNote message={problem} />}
          <div className="formactions">
            <button type="button" className="btn btn--ghost" onClick={() => setPreview(null)}>
              Cancel
            </button>
            <span className="formactions__spacer" />
            {preview.rows.length > 0 && (
              <button type="button" className="btn btn--primary" disabled={carry.isPending} onClick={() => carry.mutate(false)}>
                {carry.isPending ? 'Working…' : `Carry forward ${preview.rows.length} ${preview.rows.length === 1 ? 'balance' : 'balances'}`}
              </button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
